import { Box, TextField, useTheme } from "@material-ui/core";
import { addMonths, format, isValid, parse, startOfMonth } from "date-fns";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import getDatePattern from "../../utils/date-pattern";
import { isEmptyValue } from "../../utils/is-empty-value";
import Month from "../calendar/Month";

import { DateFilterState, TableFilterAction } from "./models";

const mask = getDatePattern(true);
const dateFormat = getDatePattern();

function DayRangeFilter(props: { filter: DateFilterState; usePopover?: boolean; dispatcher: (action: TableFilterAction) => void }) {
	const [firstCalendarMonth, setFirstCalendar] = useState<Date>(
		props.filter.startValue && props.filter.endValue && startOfMonth(props.filter.startValue) === startOfMonth(props.filter.endValue)
			? startOfMonth(addMonths(props.filter.startValue, -1))
			: startOfMonth(props.filter.startValue || addMonths(new Date(), -1))
	);
	const [secondCalendarMonth, setSecondCalendar] = useState<Date>(startOfMonth(props.filter.endValue || new Date()));
	const [startRawValue, setStartRawValue] = useState("");
	const [endRawValue, setEndRawValue] = useState("");
	const months = [firstCalendarMonth, secondCalendarMonth];
	const { t } = useTranslation();
	const { palette } = useTheme();

	useEffect(() => {
		setStartRawValue(props.filter.startValue ? format(props.filter.startValue, dateFormat) : "");
		setEndRawValue(props.filter.endValue ? format(props.filter.endValue, dateFormat) : "");
	}, [props.filter.startValue?.getTime(), props.filter.endValue?.getTime()]);

	function dispatchChangeValue(startValue: Date | null | undefined, endValue: Date | null | undefined) {
		props.dispatcher({ type: "set-values", path: props.filter.field as string, startValue, endValue });
	}

	function handleGoToNextMonth(index: number) {
		if (index === 0) {
			const newDate = addMonths(firstCalendarMonth, 1);
			setFirstCalendar(newDate);
			if (newDate >= secondCalendarMonth) {
				setSecondCalendar(addMonths(newDate, 1));
			}
			return;
		}

		setSecondCalendar(addMonths(secondCalendarMonth, 1));
	}

	function handleGoToPreviousMonths(index: number) {
		if (index === 1) {
			const newDate = addMonths(secondCalendarMonth, -1);
			setSecondCalendar(newDate);
			if (newDate <= firstCalendarMonth) {
				setFirstCalendar(addMonths(newDate, -1));
			}
			return;
		}

		setFirstCalendar(addMonths(firstCalendarMonth, -1));
	}

	function handleSelectDate(date: Date) {
		if (!!props.filter.startValue === !!props.filter.endValue || (props.filter.startValue && props.filter.startValue > date)) {
			dispatchChangeValue(date, null);
		} else {
			dispatchChangeValue(props.filter.startValue, date);
		}
	}

	function handleChangeStartDate(value: string) {
		setStartRawValue(value);
		const date = parse(value, dateFormat, new Date());
		if (isValid(date)) {
			const newEndValue = isEmptyValue(props.filter.endValue) || props.filter.endValue! < date ? null : props.filter.endValue;
			dispatchChangeValue(date, newEndValue);
			setFirstCalendar(startOfMonth(date));
			setSecondCalendar(
				!newEndValue || startOfMonth(date) === newEndValue ? startOfMonth(addMonths(date, 1)) : startOfMonth(newEndValue)
			);
		}
	}

	function handleChangeEndDate(value: string) {
		setEndRawValue(value);
		const date = parse(value, dateFormat, new Date());
		if (isValid(date)) {
			const newStartValue = isEmptyValue(props.filter.startValue) || props.filter.startValue! > date ? null : props.filter.startValue;
			dispatchChangeValue(props.filter.startValue, date);
			setSecondCalendar(startOfMonth(date));
			setSecondCalendar(
				!newStartValue || startOfMonth(date) === newStartValue ? startOfMonth(addMonths(date, -1)) : startOfMonth(newStartValue)
			);
		}
	}

	return (
		<Box>
			<Box>
				<Box display="flex" justifyContent="space-between" alignItems="center" px={4} pt={3}>
					<InputMask
						mask={mask}
						placeholder={dateFormat.toLowerCase()}
						maskPlaceholder={dateFormat.toLowerCase()}
						value={startRawValue}
						disabled={props.filter.rangeType !== "custom"}
						onChange={(e) => handleChangeStartDate(e.target.value)}
					>
						<TextField
							title="date-range-input"
							variant="outlined"
							label={t("range-from")}
							helperText=""
							InputLabelProps={{
								shrink: true,
							}}
							inputProps={{
								style: {
									color: palette.primary.main,
								},
							}}
							error={false}
						/>
					</InputMask>
					<Box mx={2}>—</Box>
					<InputMask
						mask={mask}
						placeholder={dateFormat.toLowerCase()}
						maskPlaceholder={dateFormat.toLowerCase()}
						value={endRawValue}
						disabled={props.filter.rangeType !== "custom"}
						onChange={(e) => handleChangeEndDate(e.target.value)}
					>
						<TextField
							title="date-range-input"
							variant="outlined"
							label={t("range-to")}
							helperText=""
							InputLabelProps={{
								shrink: true,
							}}
							inputProps={{
								style: {
									color: palette.primary.main,
								},
							}}
							error={false}
						/>
					</InputMask>
				</Box>
				{props.filter.rangeType === "custom" && (
					<Box>
						<Box
							display="flex"
							justifyContent="space-between"
							minWidth="520px"
							alignItems="flex-start"
							height="324px"
							padding={"24px"}
						>
							{months.map((d, index) => (
								<Month
									key={`${d.getTime()}.${index}`}
									year={d.getFullYear()}
									month={d.getMonth()}
									startDate={props.filter.startValue}
									endDate={props.filter.endValue}
									index={index}
									goToNextMonths={() => handleGoToNextMonth(index)}
									goToPreviousMonths={() => handleGoToPreviousMonths(index)}
									handleSelectDay={(date) => handleSelectDate(date)}
								/>
							))}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
}

export default memo(DayRangeFilter);
